<template>
  <div class="relative">
    <!-- Estado de carga -->
    <div v-if="loading" class="w-full">
      <!-- Título del skeleton -->
      <div v-if="title" class="mb-4">
        <h2 class="text-2xl font-bold">{{ title }}</h2>
        <p v-if="subtitle" class="text-gray-600 text-sm mb-2 md:mb-0">{{ subtitle }}</p>
      </div>
      
      <div class="flex flex-col gap-4 py-4">
        <div v-for="n in cardsPerSlide" :key="n" class="flex items-center gap-3 p-3 bg-white rounded-xl shadow animate-pulse">
          <div class="w-16 h-24 bg-gray-200 rounded-lg"></div>
          <div class="flex-1 flex flex-col gap-2">
            <div class="h-4 w-2/3 bg-gray-200 rounded"></div>
            <div class="h-3 w-full bg-gray-200 rounded"></div>
            <div class="h-3 w-1/2 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Estado de error -->
    <div v-else-if="error" class="flex flex-col items-center py-8">
      <div class="text-center">
        <div class="text-red-500 text-lg mb-2">⚠️</div>
        <p class="text-gray-600">Error al cargar los cuentos</p>
        <button @click="$emit('retry')" class="mt-2 text-blue-600 hover:text-blue-800">
          Reintentar
        </button>
      </div>
    </div>

    <!-- Carrusel -->
    <Splide v-else :options="splideOptions">
      <SplideSlide v-for="(group, idx) in groupedStories" :key="idx">
        <div class="flex flex-col gap-4">
          <router-link
            v-for="story in group"
            :key="story.id || story.title"
            :to="`/cuento/${story.documentId}`"
            class="flex items-center gap-2 bg-white rounded-xl shadow cursor-pointer hover:bg-gray-50 transition mb-px"
          >
            <img
              :src="getOptimizedImageUrl(story.image)"
              alt="cover"
              class="w-26 h-40 object-cover rounded-lg flex-shrink-0"
            />
            <div class="flex-1 flex flex-col min-w-0 py-2 pr-3">
              <div class="flex items-start justify-between mb-1 gap-2">
                <div class="flex flex-col flex-1 min-w-0">
                  <!-- Slug del taller -->
                  <div v-if="story.taller?.slug" class="mb-1">
                    <span 
                      @click.stop.prevent="handleTallerClick(story.taller.slug)"
                      class="text-xs font-medium uppercase tracking-wide cursor-pointer hover:bg-[#D17A00] transition-colors"
                      style="font-family: Inter; font-weight: 500; font-size: 9px; line-height: 110%; letter-spacing: 0.5px; color: white; background-color: #E38400; padding: 1px 5px; border-radius: 8px;"
                      :title="`Filtrar por ${formatTallerSlug(story.taller.slug)}`"
                    >
                      {{ formatTallerSlug(story.taller.slug) }}
                    </span>
                  </div>
                  <div class="card-title">
                    {{ formatTitle(story.title) }}
                  </div>
                </div>
                <button
                  @click.stop.prevent="toggleAudio(story)"
                  class="flex items-center justify-center w-6 h-6 rounded-full transition-colors flex-shrink-0"
                  :style="isCurrentAudioPlaying(story) ? 'background-color: #E38400;' : 'background-color: #000000;'"
                  :title="isCurrentAudioPlaying(story) ? 'Pausar audio' : 'Reproducir audio'"
                >
                  <svg v-if="isCurrentAudioPlaying(story)" width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                  <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <polygon points="8,5 19,12 8,19" />
                  </svg>
                </button>
              </div>
              <div class="card-description line-clamp-2 mb-2">{{ story.description || 'Sin descripción disponible' }}</div>
              <div class="flex flex-wrap gap-1 mb-2">
                <span
                  v-for="(tag, index) in getLimitedTags(story.tags)"
                  :key="tag"
                  @click.stop.prevent="handleTagClick(tag, $event)"
                  class="card-tag cursor-pointer hover:bg-gray-100 transition-colors"
                  :title="`Filtrar por ${formatTags(tag)}`"
                >
                  {{ formatTags(tag) }}
                </span>
                <span v-if="story.tags && story.tags.length > 2" class="card-tag text-xs" style="color: #999;">+{{ story.tags.length - 2 }}</span>
              </div>
              <div class="flex items-center justify-between mt-2">
                <div class="flex items-center gap-1">
                  <img
                    v-if="story.alumno?.idUsuario && story.alumno?.imageLoaded !== false"
                    :src="getAvatarUrl(story.alumno)"
                    alt="Avatar del autor"
                    class="rounded-full w-7 h-7 object-cover"
                    @error="handleImageError($event, story)"
                    @load="handleImageLoad($event, story)"
                  />
                  <div
                    v-if="story.alumno?.idUsuario && story.alumno?.imageLoaded === false"
                    class="rounded-full w-7 h-7 flex items-center justify-center text-xs font-medium text-white"
                    style="background-color: #E38400;"
                  >
                    {{ getInitials(story.alumno.nombre, story.alumno.apellido) }}
                  </div>
                  <span class="card-author">Por {{ formatAuthorName(story.author) }}</span>
                </div>
                <!-- Puntaje y estrella - solo mostrar si hay valoraciones -->
                <div v-if="getStoryRating(story.documentId) !== '0.0'" class="flex items-center ml-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="black" viewBox="0 0 24 24" width="14" height="14" class="mr-1"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                  <span class="font-semibold text-base" style="font-size: 12px; color: #222;">
                    {{ getStoryRating(story.documentId) }}
                  </span>
                </div>
              </div>
            </div>
          </router-link>
        </div>
      </SplideSlide>
    </Splide>
  </div>
</template>

<script setup>
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';
import { computed, defineProps, defineEmits, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAudioPlayerStore } from '@/stores/audioPlayer';
import { useValoracionesStore } from '@/stores/valoraciones';
import { useAuthStore } from '@/stores/auth';


const props = defineProps({
  stories: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: Boolean,
    default: false
  },
  cardsPerSlide: {
    type: Number,
    default: 3
  },
  title: {
    type: String,
    default: ''
  },
  subtitle: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['retry']);
const router = useRouter();
const audioStore = useAudioPlayerStore();
const valoracionesStore = useValoracionesStore();
const authStore = useAuthStore();

// Función para formatear títulos automáticamente
const formatTitle = (title) => {
  if (!title) return '';
  
  let formattedTitle = title;
  
  // Si el título está completamente en mayúsculas, formatearlo
  if (title === title.toUpperCase() && title.length > 3) {
    formattedTitle = title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();
  }
  
  // Contar solo letras y números (excluyendo espacios y signos de puntuación)
  const lettersOnly = formattedTitle.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]/g, '');
  
  // Solo truncar si hay más de 14 letras/números
  if (lettersOnly.length > 18) {
    // Encontrar el último espacio antes de la posición 14 para cortar en una palabra completa
    let cutIndex = 18;
    while (cutIndex > 0 && formattedTitle[cutIndex] !== ' ') {
      cutIndex--;
    }
    if (cutIndex === 0) cutIndex = 18; // Si no hay espacio, cortar en 14
    
    return formattedTitle.substring(0, cutIndex) + '...';
  }
  
  return formattedTitle;
};

// Función para formatear nombres de autores automáticamente
const formatAuthorName = (name) => {
  if (!name) return '';
  
  // Dividir el nombre en palabras
  const words = name.split(' ');
  
  // Formatear cada palabra: primera letra mayúscula, resto minúsculas
  const formattedWords = words.map(word => {
    if (!word) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
  
  return formattedWords.join(' ');
};

                // Función para formatear el slug del taller
                const formatTallerSlug = (slug) => {
                  if (!slug) return '';

                  // Mapeo de slugs a nombres más amigables (todo en minúsculas)
                  const mapeo = {
                    'taller-1': 'taller enero',
                    'taller-2': 'taller febrero',
                    'taller-mayo': 'taller mayo',
                    'taller-junio': 'taller junio',
                    'taller-julio': 'taller julio',
                    'taller-agosto': 'taller agosto',
                    'taller-septiembre': 'taller septiembre',
                    'taller-octubre': 'taller octubre',
                    'taller-noviembre': 'taller noviembre',
                    'taller-diciembre': 'taller diciembre',
                    'taller-enero': 'taller enero',
                    'taller-febrero': 'taller febrero',
                    'taller-marzo': 'taller marzo',
                    'taller-abril': 'taller abril'
                  };

                  // Si existe en el mapeo, usar el nombre formateado
                  if (mapeo[slug]) {
                    return mapeo[slug];
                  }

                  // Si no existe, formatear el slug: convertir guiones en espacios y todo en minúsculas
                  return slug
                    .replace(/-/g, ' ')
                    .toLowerCase();
                };



const reproducirCuento = (story) => {
  audioStore.playCuento(story);
};

// Función para alternar entre reproducir y pausar
const toggleAudio = (story) => {
  // Si es el audio actual y está reproduciéndose, pausarlo
  if (isCurrentAudioPlaying(story)) {
    audioStore.pause();
  } else {
    // Si no es el audio actual o está pausado, reproducirlo
    audioStore.playCuento(story);
  }
};

// Función para verificar si el audio actual está reproduciéndose
const isCurrentAudioPlaying = (story) => {
  return audioStore.currentCuento?.documentId === story.documentId && audioStore.isPlaying;
};

// Función para limitar tags a máximo 2
const getLimitedTags = (tags) => {
  if (!tags || !Array.isArray(tags)) return [];
  return tags.slice(0, 2);
};

// Función para formatear tags para mostrar
const formatTags = (tag) => {
  const mapeo = {
    'nostalgico': 'Nostálgico',
    'agridulce': 'Agridulce',
    'inspirador': 'Inspirador',
    'intimista': 'Intimista',
    'ingenioso': 'Ingenioso',
    'suspenso': 'Suspenso',
    'optimista': 'Optimista',
    'divertido': 'Divertido',
    'melodramatico': 'Melodramático',
    'sentimental': 'Sentimental',
    'misterioso': 'Misterioso',
    'dramatico': 'Dramático',
    'tragico': 'Trágico',
    'poetico': 'Poético',
    'existencial': 'Existencial',
    'reflexivo': 'Reflexivo',
    'filosofico': 'Filosófico',
    'absurdo': 'Absurdo',
    'realista': 'Realista',
    'crudo': 'Crudo',
    'violento': 'Violento',
    'politico': 'Político',
    'social': 'Social',
    'familiar': 'Familiar',
    'adolescencia': 'Adolescencia',
    'vejez': 'Vejez',
    'infantil': 'Infantil',
    'fantastico': 'Fantástico',
    'magico': 'Mágico',
    'distopico': 'Distópico',
    'futurista': 'Futurista',
    'retro': 'Retro',
    'historico': 'Histórico',
    'costumbrista': 'Costumbrista',
    'urbano': 'Urbano',
    'rural': 'Rural',
    'epico': 'Épico',
    'intimo': 'Íntimo',
    'lgbtq': 'LGBTQ+',
    'feminista': 'Feminista',
    'espiritual': 'Espiritual',
    'religioso': 'Religioso',
    'experimental': 'Experimental',
    'minimalista': 'Minimalista',
    'grandilocuente': 'Grandilocuente',
    'onirico': 'Onírico',
    'argentinidad': 'Argentinidad',
    'de-epoca': 'De época',
    'autobiografico': 'Autobiográfico',
    'regional': 'Regional',
    'coral': 'Coral',
    'road-movie': 'Road movie',
    'psicologico': 'Psicológico',
    'de-aventuras': 'De aventuras',
    'de-investigacion': 'De investigación',
    'de-encierro': 'De encierro',
    'animalista': 'Animalista',
    'inclusivo': 'Inclusivo',
    'con-critica-social': 'Con crítica social',
    'viajes': 'Viajes'
  };
  return mapeo[tag.toLowerCase()] || tag;
};

// Función para manejar el clic en tags
const handleTagClick = (tag, event) => {
  // Detener la propagación del evento de manera explícita
  event.stopPropagation();
  event.preventDefault();
  
  console.log('🏷️ Click en tag detectado:', { tag: tag, event: event });
  
  // El tag ya es el value original, asegurar que esté en minúsculas
  const tagValue = tag.toLowerCase();
  console.log('🏷️ Navegando a filtros con tag:', { tagValue: tagValue });
  
  router.push({
    path: '/cuento/filtrar',
    query: { tags: tagValue }
  }).then(() => {
    console.log('✅ Navegación exitosa a filtros con tag:', tagValue);
  }).catch((error) => {
    console.error('❌ Error al navegar a filtros:', error);
  });
};

// Función para manejar clic en badge de taller
const handleTallerClick = (tallerSlug) => {
  console.log('🏫 Click en badge de taller:', { tallerSlug: tallerSlug });
  
  // Navegar a la página de filtros con el parámetro tallerSlug
  router.push({
    path: '/cuento/filtrar',
    query: { tallerSlug: tallerSlug }
  }).then(() => {
    console.log('✅ Navegación exitosa a filtros con tallerSlug:', tallerSlug);
  }).catch((error) => {
    console.error('❌ Error al navegar a filtros:', error);
  });
};

// Función para navegar a la página de filtros con un tag seleccionado
const navigateToFilterWithTag = (tag) => {
  // El tag ya es el value original, asegurar que esté en minúsculas
  const tagValue = tag.toLowerCase();
  console.log('🏷️ Navegando a filtros con tag:', { tagValue: tagValue });
  
  router.push({
    path: '/cuento/filtrar',
    query: { tags: tagValue }
  }).then(() => {
    console.log('✅ Navegación exitosa a filtros con tag:', tagValue);
  }).catch((error) => {
    console.error('❌ Error al navegar a filtros:', error);
  });
};

function getOptimizedImageUrl(imageUrl) {
  if (!imageUrl) return '';
  if (imageUrl.includes('/small_')) return imageUrl;
  if (imageUrl.includes('afiche_')) return imageUrl.replace('afiche_', 'small_afiche_');
  try {
    const url = new URL(imageUrl);
    const pathParts = url.pathname.split('/');
    const fileName = pathParts[pathParts.length - 1];
    if (fileName && !fileName.startsWith('small_')) {
      pathParts[pathParts.length - 1] = `small_${fileName}`;
      url.pathname = pathParts.join('/');
      return url.toString();
    }
  } catch (e) {}
  return imageUrl;
}

const groupedStories = computed(() => {
  // Limitar a máximo 9 items
  const limitedStories = props.stories.slice(0, 9);
  
  // Log para debugging - mostrar información de los autores
  console.log('📚 AudioStoriesCarouselMobile - Autores de los cuentos:', limitedStories.map(story => ({
    title: story.title,
    author: story.author,
    idUsuario: story.idUsuario,
    hasIdUsuario: !!story.idUsuario,
    alumnoData: story.alumno
  })));
  
  const groups = [];
  for (let i = 0; i < limitedStories.length; i += props.cardsPerSlide) {
    groups.push(limitedStories.slice(i, i + props.cardsPerSlide));
  }
  return groups;
});

const splideOptions = {
  perPage: 1,
  gap: '1rem',
  drag: true,
  autoWidth: false,
  autoHeight: true,
  arrows: false,
  pagination: true,
  type: 'loop',
  padding: { right: '10%' },
};

// Función para formatear el ID del usuario a 6 dígitos
const formatUserId = (id) => {
    const numericId = parseInt(id, 10);
    return String(numericId).padStart(6, "0");
};

// Función para obtener la URL del avatar del usuario
const getAvatarUrl = (alumno) => {
    if (!alumno?.idUsuario) return '';
    
    // Caso especial para Ignacio Champane (ID: 8699)
    if (alumno.idUsuario === "8699" || alumno.idUsuario === 8699) {
        return "https://files.hernancasciari.com/img/usuarios/020051.jpg";
    }
    
    // Para todos los demás usuarios, usar el formato estándar
    return `https://files.hernancasciari.com/img/usuarios/${formatUserId(alumno.idUsuario)}.jpg`;
};

// Función para obtener iniciales del nombre
const getInitials = (nombre, apellido) => {
    const firstInitial = nombre ? nombre.charAt(0).toUpperCase() : '';
    const lastInitial = apellido ? apellido.charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial;
};

// Función para manejar errores de imagen
const handleImageError = (event, story) => {
    const img = event.target;
    const originalSrc = img.src;
    
    // Si es una imagen de usuario (contiene idUsuario)
    if (originalSrc.includes('files.hernancasciari.com/img/usuarios/')) {
        // Marcar que no se cargó para mostrar iniciales
        if (story && story.alumno) {
            story.alumno.imageLoaded = false;
        }
    } else {
        // Si es una imagen optimizada de cuento
        if (originalSrc.includes('small_')) {
            const originalUrl = originalSrc.replace('small_', '');
            img.src = originalUrl;
        } else {
            img.src = '/src/assets/images/Sonoro/anecdota.png';
        }
    }
};

// Función para manejar carga exitosa de imagen de usuario
const handleImageLoad = (event, story) => {
    if (story && story.alumno) {
        story.alumno.imageLoaded = true;
    }
};

// Función para obtener el rating de un cuento
const getStoryRating = (documentId) => {
  if (!documentId) return '0.0';
  
  const estadisticas = valoracionesStore.getEstadisticasByDocumentId(documentId);
  return estadisticas.promedio > 0 ? estadisticas.promedio.toFixed(1) : '0.0';
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.splide__arrows {
  display: none !important;
}

/* Estilos para los dots de paginación - Especificidad máxima */
.splide__pagination.splide__pagination--ltr {
  display: flex !important;
  justify-content: center !important;
  padding: 0.5rem 0 !important;
  position: relative !important;
  bottom: 10px !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  z-index: 10 !important;
}

.splide__pagination__page.splide__pagination__page {
  width: 8px !important;
  height: 8px !important;
  background-color: #d1d5db !important;
  border-radius: 50% !important;
  margin: 0 4px !important;
  transition: all 0.3s ease !important;
  border: none !important;
  padding: 0 !important;
  opacity: 1 !important;
  position: relative !important;
}

.splide__pagination__page.splide__pagination__page.is-active {
  background-color: #E38400 !important;
  transform: scale(1.2) !important;
  opacity: 1 !important;
}

/* Estilos globales para forzar la sobrescritura */
:deep(.splide__pagination) {
  bottom: auto !important;
  position: relative !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding: 0.5rem 0 !important;
}

:deep(.splide__pagination__page) {
  width: 8px !important;
  height: 8px !important;
  background-color: #d1d5db !important;
  margin: 0 4px !important;
}

:deep(.splide__pagination__page.is-active) {
  background-color: #E38400 !important;
  transform: scale(1.2) !important;
}

.card-title {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-style: bold;
  font-size: 13px;
  line-height: 110%;
  letter-spacing: 0%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.card-description {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 11px;
  line-height: 130%;
  letter-spacing: 0%;
  color: #696969;
}
.card-author {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-style: normal;
  font-size: 10px;
  line-height: 110%;
  letter-spacing: 0%;
}
.card-tag {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-style: normal;
  font-size: 10px;
  line-height: 110%;
  letter-spacing: 0%;
  text-align: center;
  color: #969696;
  border: 1px solid #969696;
  border-radius: 999px;
  padding: 1px 5px;
  background: #fff;
  display: inline-block;
}
</style> 